import { Report, ReportInternalType, ReportStatus } from '@/__generated__/graphql';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PageLoader } from '@/components/shared/PageLoader';
import { ReportCard } from './ReportCard';
import { NoReportFound } from '../shared/NoReportFound';
import { useFilteredReportsByType } from '../hooks/useFilteredReportsByType';

type ReportsListProps = {
  selectedReportType: ReportInternalType;
  status: ReportStatus | 'ALL';
  searchTerm?: string;
  groupId?: string;
  groupName?: string;
  selectedReportIds?: string[];
  setSelectedReportIds?: React.Dispatch<React.SetStateAction<string[]>>;
};

export const ReportsList = ({
  selectedReportType,
  status,
  searchTerm,
  groupId,
  groupName,
  selectedReportIds = [],
  setSelectedReportIds = () => {},
}: ReportsListProps) => {
  const { reports, loading, error } = useFilteredReportsByType(
    selectedReportType,
    status,
    searchTerm,
    groupId,
    groupName
  );

  if (loading && reports?.length === 0) {
    return (
      <Stack direction="row" justifyContent="center" marginTop={8}>
        <PageLoader />
      </Stack>
    );
  }

  if (!loading && (!reports || error)) {
    return (
      <Stack justifyContent="center" marginTop="20%" textAlign="center">
        <Typography my={2} variant="h6">
          An unknown error has occurred, please contact support
        </Typography>
        <Typography>Error code: {error?.message || 'N/A'}</Typography>
      </Stack>
    );
  }

  const handleCheckboxChange = (reportId: string, checked: boolean) => {
    if (checked) {
      setSelectedReportIds((prev) => [...prev, reportId]);
    } else {
      setSelectedReportIds((prev) => prev.filter((id) => id !== reportId));
    }
  };

  return (
    <Grid container rowSpacing={3} columnSpacing={4} paddingX={1}>
      {reports.length === 0 ? (
        <Grid size={{ xs: 12 }} display="flex" justifyContent="center" alignItems="center">
          <NoReportFound />
        </Grid>
      ) : (
        <>
          {reports.map((report) => {
            return (
              <Grid key={report?.id} size={{ xs: 12, sm: 6, md: 3, lg: 3, xl: 3 }}>
                <ReportCard
                  report={report as Report}
                  renderCheckBox={true}
                  isChecked={selectedReportIds.includes(report?.id || '')}
                  onCheckboxChange={handleCheckboxChange}
                />
              </Grid>
            );
          })}
        </>
      )}
    </Grid>
  );
};
